import { getSalesEffectiveDate } from '@store/sar-store';
import { UserCoverageAndSelections } from '../use-coverage';
import { Category, Warranty, anyCarvanaCare, anyManufacturer, SubCategory, SubSubCategory } from '../../../constants';

export enum CoverageScreenType {
  contactUs = 'contactUs',
  limited100Covered = 'limited100Covered',
  manufacturerCovered = 'manufacturerCovered',
  carvanaCareCovered = 'carvanaCareCovered',
  carvanaCareOilTireCovered = 'carvanaCareOilTireCovered',
  thirtyDayCovered = 'thirtyDayCovered',
  conditionallyCoveredBrakes = 'conditionallyCoveredBrakes',
  notCovered = 'notCovered',
  notCoveredCosmetic = 'notCoveredCosmetic',
  notCoveredGlass = 'notCoveredGlass',
  notCoveredAdditionalCosmetic = 'notCoveredAdditionalCosmetic',
  notCoveredAdditionalKey = 'notCoveredAdditionalKey',
  notCoveredMissingAccessories = 'notCoveredMissingAccessories',
  cosmeticSubmitted = 'cosmeticSubmitted',
  carvanaCareRoadHazardCovered = 'carvanaCareRoadHazardCovered',
  carvanaCareRoadHazardNotCovered = 'carvanaCareRoadHazardNotCovered',
  carvanaCareOilChangeCovered = 'carvanaCareOilChangeCovered',
  carvanaCareOilChangeNotCovered = 'carvanaCareOilChangeNotCovered',
  carvanaCareDentCovered = 'carvanaCareDentCovered',
}

export const getType = ({
  category,
  subcategory,
  subsubcategory,
  warranty,
  reportedConcernCount,
  glassConcernCount,
  saleEffectiveDate,
}: UserCoverageAndSelections): CoverageScreenType => {
  const isWithin7Days = saleEffectiveDate ? getSalesEffectiveDate(new Date(saleEffectiveDate), 7) : false;

  switch (category) {
    case Category.MechanicalAndElectrical:
      if (warranty === Warranty.LimitedWarranty) return CoverageScreenType.limited100Covered;
      if (anyManufacturer(warranty)) return CoverageScreenType.manufacturerCovered;
      if (anyCarvanaCare(warranty)) return CoverageScreenType.carvanaCareCovered;
      return CoverageScreenType.notCovered;
    case Category.Battery:
      if (subcategory === SubCategory.ElectricOrHybrid) {
        if (warranty === Warranty.ThirtyDay) return CoverageScreenType.manufacturerCovered;
        if (warranty === Warranty.LimitedWarranty) return CoverageScreenType.manufacturerCovered;
        if (warranty === Warranty.OEMPowerTrain) return CoverageScreenType.manufacturerCovered;
        if (anyCarvanaCare(warranty)) return CoverageScreenType.manufacturerCovered;
        return CoverageScreenType.notCovered;
      }
      if (warranty === Warranty.ThirtyDay) return CoverageScreenType.thirtyDayCovered;
      if (warranty === Warranty.LimitedWarranty) return CoverageScreenType.limited100Covered;
      if (anyManufacturer(warranty)) return CoverageScreenType.manufacturerCovered;
      if (anyCarvanaCare(warranty)) return CoverageScreenType.carvanaCareCovered;
      return CoverageScreenType.notCovered;
    case Category.HeatingAndCooling:
      if (warranty === Warranty.LimitedWarranty) return CoverageScreenType.limited100Covered;
      if (anyManufacturer(warranty)) return CoverageScreenType.manufacturerCovered;
      if (anyCarvanaCare(warranty)) return CoverageScreenType.carvanaCareCovered;
      return CoverageScreenType.notCovered;
    case Category.Maintenance:
      if (warranty === Warranty.CarvanaCarePremier || warranty === Warranty.CarvanaCareCosmetic) {
        if (!isWithin7Days) return CoverageScreenType.carvanaCareOilChangeCovered;
        return CoverageScreenType.carvanaCareOilChangeNotCovered;
      }
      if (anyCarvanaCare(warranty)) return CoverageScreenType.carvanaCareOilTireCovered;
      return CoverageScreenType.notCovered;
    case Category.TiresOrBrakes:
      if (subcategory === SubCategory.Tires || subcategory === SubCategory.Wheels) {
        if (
          subsubcategory === SubSubCategory.TireDamageYes &&
          (warranty === Warranty.CarvanaCarePremier || warranty === Warranty.CarvanaCareCosmetic)
        ) {
          return CoverageScreenType.carvanaCareRoadHazardCovered;
        }

        if (subsubcategory === SubSubCategory.TireDamageNo) {
          return CoverageScreenType.carvanaCareRoadHazardNotCovered;
        }
      }
      if (warranty === Warranty.ThirtyDay) return CoverageScreenType.thirtyDayCovered;
      if (warranty === Warranty.LimitedWarranty && subcategory === SubCategory.Brakes)
        return CoverageScreenType.conditionallyCoveredBrakes;
      if (anyCarvanaCare(warranty) && subcategory === SubCategory.Brakes)
        return CoverageScreenType.conditionallyCoveredBrakes;
      return CoverageScreenType.notCovered;
    case Category.Cosmetic: {
      if (subsubcategory === SubSubCategory.Dents) {
        if (isWithin7Days) return CoverageScreenType.notCoveredCosmetic;
        return CoverageScreenType.carvanaCareDentCovered;
      }
      const sevenDayOrAsrExtensionCoverage = warranty === Warranty.SevenDay || warranty === Warranty.ASRExtension;
      if (subcategory === SubCategory.Glass) {
        if (!isWithin7Days || glassConcernCount >= 1) {
          return CoverageScreenType.notCoveredGlass;
        }
        return CoverageScreenType.cosmeticSubmitted;
      }
      if (anyCarvanaCare(warranty)) return CoverageScreenType.carvanaCareCovered;
      if (reportedConcernCount >= 3 && sevenDayOrAsrExtensionCoverage)
        return CoverageScreenType.notCoveredAdditionalCosmetic;
      if (sevenDayOrAsrExtensionCoverage) return CoverageScreenType.cosmeticSubmitted;
      return CoverageScreenType.notCoveredCosmetic;
    }
    case Category.Accessories:
      if (anyManufacturer(warranty)) return CoverageScreenType.manufacturerCovered;
      if (warranty === Warranty.SevenDay) return CoverageScreenType.contactUs;
      if (warranty === Warranty.LimitedWarranty) return CoverageScreenType.limited100Covered;
      if (subcategory === SubCategory.AdditionalKey) return CoverageScreenType.notCoveredAdditionalKey;
      return CoverageScreenType.notCoveredMissingAccessories;
    default:
      return CoverageScreenType.contactUs;
  }
};
